import { useCallback, useEffect, useRef } from 'react';
import { areDependencyListsEqual } from './are-dependency-lists-equal';

type Callback<R> = (response: R) => any;
type CallbackWithDeps<R> = (response: R, requestDeps: React.DependencyList) => any;

/**
 * This hook can be used to create a function and call the function later
 * only if a set of parameters match between creation time and invokation time.
 * The typical use case is to make an API call and only handle the response
 * if the request params are still valid.
 *
 * @param callback The function to execute if deps match
 * @param deps deps at the time of creating the callback
 * @returns
 */
export function useDepsMatchingCallbackRef<R>(
  callback: Callback<R>,
  deps: React.DependencyList
): React.MutableRefObject<CallbackWithDeps<R> | undefined> {
  const depsMatchingCallbackRef = useRef<CallbackWithDeps<R>>();

  const onApiResponse = useCallback(
    (response: R, requestDeps: React.DependencyList) => {
      if (areDependencyListsEqual(requestDeps, deps)) {
        callback(response);
      }
    },
    [callback, deps]
  );

  useEffect(() => {
    depsMatchingCallbackRef.current = onApiResponse;
  }, [onApiResponse]);

  return depsMatchingCallbackRef;
}
